<i18n>
{
  "en": {
    "forecastGroupTitle": "Forecast groups",
    "forecastLoadingError": "Error while loading forecast groups data",
    "forecastError": "Error while generating forecast groups data",
    "noServices": "No services available",
    "noGroups": "No groups forecasts for this period and service",
    "saveGroupsSuccess": "Forecast groups were saved successfully",
    "saveGroupsError": "Error saving forecast groups. Please, try it later.",
    "redirectWarning": "The specified period was not available",
    "confirmTitle": "Confirming changes for multiple services",
    "confirmDescription": "Changes will be saved for services",
    "confirm": "Confirm",
    "generatingGroups": "Recommendations are being generated",
    "errorRecommendations": "There was an error generating recommendations",
    "periodLoadingError": "Error updating period data",
    "stillPredicting": "Some predictions are still ongoing, please, try again later"
  },
  "es": {
    "forecastGroupTitle": "Previsión de grupos",
    "forecastLoadingError": "Error en la carga de predicción de grupos",
    "forecastError": "Error en la predicción de grupos",
    "noServices": "No hay servicios disponibles",
    "noGroups": "No hay previsiones de grupos para este periodo y servicio",
    "saveGroupsSuccess": "Se guardaron los grupos correctamente",
    "saveGroupsError": "Error guardando la previsión de grupos. Inténtelo de nuevo más tarde",
    "redirectWarning": "El periodo seleccionado no estaba disponible",
    "confirmTitle": "Confirmando cambios para varios servicios",
    "confirmDescription": "Se guardarán los cambios para los servicios",
    "confirm": "Confirmar",
    "generatingGroups": "Las recomendaciones se están generando",
    "errorRecommendations": "Hubo algún error en la generación de recomendaciones",
    "periodLoadingError": "Error actualizando datos de periodo",
    "stillPredicting": "Aún se están generando datos de predicciones, por favor, inténtelo más tarde"
  }
}
</i18n>
<template>

  <v-container>
    <div class="text-center font-weight-bold ma-1 text-md-h6 text-body-1">
      {{ $t('forecastGroupTitle') }}
    </div>
    <div class="text-center font-weight-bold ma-1 text-md-h6 text-body-1" v-if="selectedPeriod"> {{
        selectedPeriod.name
      }}
    </div>

    <div v-if="emptyServiceList && !isLoading" class="d-flex justify-space-around grey--text">
      <span>{{ $t('noServices') }}</span>
    </div>
    <div v-else class="d-flex justify-space-around mb-1">
      <dynameat-dropdown :itemSelected="selectedService" element-key="guid" element-label="name"
                         :list="serviceList" @on-select="onSelectService" :use-buttons="true"/>
    </div>
    <dynameat-skeleton v-if="isLoading" skeleton-type="forecastDemand" :container="false"
                       class="mt-5"/>
    <div v-if="forecastGroupListByServiceEmpty && !isLoading"
         class="d-flex justify-space-around mt-1 grey--text">
      <span>{{ $t('noGroups') }}</span>
    </div>

    <v-list v-else class="mb-16">
      <div v-if="showCards">
        <forecast-group-card v-for="demand in forecastGroupListByService" :key="demand.day"
                              :demand="demand"
                              @demand-changed="onDemandChange"
                              :selected-service-guid="selectedService.guid"
                              :disabled="!canValidateGroup || !demand.isDayEditable()"
                              :event-occurrences="initialEventOccurrenceList"
                              class="mb-2"/>
      </div>
    </v-list>

    <dynameat-confirmation-modal :visible="confirmModalVisible"
                                 :title="$t('confirmTitle')"
                                 :description="confirmDescription"
                                 :confirm-text="$t('confirm')"
                                 :is-loading="isLoading"
                                 @confirm="onSave"
                                 @cancel="onToggleModal"/>

    <dynameat-confirmation-modal :visible="recommendationModalVisible"
                                 :title="$t('generatingGroups')"
                                 :is-loading="isLoading"
    />
    <dynameat-confirmation-buttons @cancel="onCancel" @confirm="onToggleModal"
                                   :confirm-enabled="canValidateGroup"/>
  </v-container>

</template>

<script>
import DynameatConfirmationButtons from '@dynameat/components/DynameatConfirmationButtons';
import {
  createErrorNotification,
  createInfoNotification,
  createSuccessNotification
} from '@ulibs/unnotificationsqueue';
import DynameatDropdown from '@dynameat/components/DynameatDropdown';
import {
  checkPeriodMixin,
  periodMixins,
  serviceMixins
} from '@pointOfSale/mixins';
import cloneDeep from 'lodash/cloneDeep';
import { routeOutConfirmationMixin } from '@core/mixins';
import DynameatSkeleton from '@dynameat/components/DynameatSkeleton';
import Component from 'vue-class-component';
import Vue from 'vue';
import {
  Components, MapActions, MapGetters, Mixins
} from '@core/helpers/decorators';
import { Prop, Watch } from 'vue-property-decorator';
import DynameatConfirmationModal from '@dynameat/components/DynameatConfirmationModal';
import VueRouter from 'vue-router';
import ForecastGroupCard from './components/ForecastGroupCard.vue';

const { isNavigationFailure, NavigationFailureType } = VueRouter;

Component.registerHooks(['beforeRouteLeave']);

@Component
@Components({
  DynameatSkeleton,
  DynameatDropdown,
  ForecastGroupCard,
  DynameatConfirmationButtons,
  DynameatConfirmationModal
})
@Mixins([periodMixins, serviceMixins, routeOutConfirmationMixin,
  checkPeriodMixin('demand', ['price', 'menu'])])
@MapGetters(['forecastDemandList', 'isLoading', 'eventOccurrenceList'])
@MapActions(['addNotification', 'forecastDemandListRequest', 'setLoading', 'editForecastDemandList',
  'saveForecastDemandListRequest', 'eventOccurrenceListRequest'])
export default class GroupForecaster extends Vue {
  @Prop({ type: Object, required: false, default: () => null }) originRouteProp;

  selectedService = null;

  initialForecastGroupList = [];

  confirmModalVisible = false;

  initialEventOccurrenceList = [];

  get confirmDescription() {
    const serviceNames = this.serviceList.map((el) => `"${el.name}"`);
    return `${this.$t('confirmDescription')}: ${serviceNames.join(', ')}`;
  }

  get forecastGroupListByService() {
    if (this.selectedService) {
      return this.forecastDemandList.filter((demand) => demand.hasMatchingServices(this.selectedService.guid));
    }
    return [];
  }

  get forecastGroupListByServiceEmpty() {
    return this.forecastGroupListByService.length === 0;
  }

  get canValidateGroup() {
    return this.selectedPeriod?.isDemandEditable() && this.forecastDemandList.length !== 0;
  }

  get showCards() {
    return !this.isLoading && !this.forecastGroupListByServiceEmpty;
  }

  get originRoute() {
    return this.originRouteProp || {
      name: 'optimizer',
      params: { periodGuid: this.$route.params.periodGuid }
    };
  }

  async created() {
    if (this.serviceList.length > 0) this.selectedService = this.serviceList[0];
    if (!await this.checkPreconditions()) {
      this.addNotification(createInfoNotification(this.$t('stillPredicting')));
      this.onCancel();
    }
    const shouldContinue = await this.reloadPeriod();
    if (shouldContinue) {
      await this.getForecastDemandData();
    }
  }

  beforeRouteLeave(to, from, next) {
    if (this.waiter) this.waiter.forceClear();
    if (this.isLoading) {
      this.setLoading(-1);
    }
    next();
  }

  @Watch('serviceList')
  onServiceListChanged(updatedServiceList) {
    if (updatedServiceList.length > 0) this.selectedService = updatedServiceList[0];
  }

  @Watch('hasForecastDemandChanged')
  onHasForecastDemandChanged(hasChanged) {
    this.allowRouteExitWithoutConfirmation = !hasChanged;
  }

  @Watch('$route')
  onRouteChange(to, from) {
    if (to.params.periodGuid !== from.params.periodGuid) {
      this.getForecastDemandData();
    }
  }

  async getForecastDemandData() {
    try {
      await this.setLoading(1);
      const forecastDemandList = await this.forecastDemandListRequest(this.$route.params.periodGuid);
      this.initialForecastGroupList = cloneDeep(forecastDemandList);
      await this.setLoading(-1);
    } catch (e) {
      if (this.isLoading) {
        this.setLoading(-1);
      }
    }
    try {
      await this.setLoading(1);
      const eventOccurrenceList = await this.eventOccurrenceListRequest(this.$route.params.periodGuid);
      this.initialEventOccurrenceList = cloneDeep(eventOccurrenceList);
      await this.setLoading(-1);
    } catch (e) {
      if (this.isLoading) {
        this.setLoading(-1);
      }
    }
  }

  onToggleModal() {
    this.confirmModalVisible = !this.confirmModalVisible;
  }

  onSelectService(selectedService) {
    this.selectedService = selectedService;
  }

  onDemandChange(groupInfoList) {
    this.editForecastDemandList({
      sliders: groupInfoList,
      selectedServiceGuid: this.selectedService.guid
    });
  }

  async onSave() {
    try {
      await this.setLoading(1);
      await this.saveForecastDemandListRequest(this.$route.params.periodGuid);
      await this.setLoading(-1);
      this.addNotification(createSuccessNotification(this.$t('saveGroupsSuccess')));
    } catch (err) {
      if (err.response.status === 409) this.addNotification(createErrorNotification(this.$t('stillPredicting')));
      else this.addNotification(createErrorNotification(this.$t('saveGroupsError')));
    } finally {
      this.allowRouteExitWithoutConfirmation = true;
      await this.$router.push(this.originRoute);
    }
  }

  onCancel() {
    this.$router.push(this.originRoute)
      .catch((e) => {
        if (!isNavigationFailure(e, NavigationFailureType.aborted)) {
          Promise.reject(e);
        }
      });
  }
}
</script>
